import React, { useState } from "react";

import {
  Checkbox,
  Divider,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  Stack,
  Typography,
} from "@mui/material";
import { HighlightOffTwoTone as CloseIcon } from "@mui/icons-material";

import Button from "components/button";
import ScrollableModal from "components/modals/scrollableModal";
import ExcelExport from "features/components/excelExport";
import {
  OrdersExportProps,
  OrdersExportColumnsType,
} from "features/products/index.types";
import {
  allColumnCategoryCheckboxStyles,
  footerCancelBtnStyle,
  footerGrid12Style,
  headerGrid12Style,
} from "features/products/index.styles";
import { orderListingColumns } from "utils/constants";

const CustomColumnsExport = ({
  ordersList,
  setExportType,
}: OrdersExportProps) => {
  const [ordersColumnsList, setOrdersColumnsList] = useState<
    OrdersExportColumnsType[]
  >(JSON.parse(JSON.stringify(orderListingColumns)));
  const [exportItems, setExportItems] = useState<boolean>(false);

  const [toggleItemsModal, setToggleItemsModal] = useState<boolean>(true);

  //======================================= Handler Functions =======================================//

  const handleItemsExport = () => {
    setExportItems(true);
    handleToggleItemsModal();
  };

  const handleToggleItemsModal = () => {
    setToggleItemsModal((state) => !state);
  };

  // handle single column checkbox selection
  const handleColumnSelection = (
    event: React.ChangeEvent<HTMLInputElement>,
    selectedCheckboxIndex: number
  ) => {
    setOrdersColumnsList(
      ordersColumnsList.map((column, columnIndex) => {
        if (columnIndex === selectedCheckboxIndex) {
          return {
            ...column,
            export: event.target.checked,
          };
        }

        return column;
      })
    );
  };
  // };

  // function to handle all column/category checkbox selection
  const handleAllColumnSelection = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const selectedColumns: OrdersExportColumnsType[] = [];

    ordersColumnsList.forEach((column) => {
      if (column.key !== "order_id" && column.key !== "name") {
        selectedColumns.push({
          ...column,
          export: event.target.checked,
        });
      } else {
        selectedColumns.push(column);
      }
    });

    setOrdersColumnsList(selectedColumns);
  };

  // handle all column/category checkbox selection state with respect to single checkbox selection
  const handleCheckboxes = () => {
    return ordersColumnsList.every((column) => column.export);
  };

  // returns the checkboxes with orders columns
  const exportUIList = (itemsExportData: OrdersExportColumnsType[]) => {
    return (
      <>
        <FormControlLabel
          control={
            <Checkbox
              checked={handleCheckboxes()}
              onChange={(event) => handleAllColumnSelection(event)}
            />
          }
          label={
            <>
              <Typography variant="h3" sx={{ color: "#212121" }}>
                All Columns
              </Typography>
            </>
          }
          sx={allColumnCategoryCheckboxStyles}
        />

        <FormGroup>
          <Grid
            container
            sx={{ mb: "30px" }}
            spacing={{ xs: 1 }}
            columns={{ xs: 2 }}
          >
            {itemsExportData?.map((item, itemIndex) => (
              <Grid
                item
                xs={1}
                key={itemIndex}
                sx={{ p: "2px 0px 0px 8px !important" }}
              >
                <FormControlLabel
                  label={item.header}
                  control={
                    <Checkbox
                      checked={item.export}
                      onChange={(event) =>
                        handleColumnSelection(event, itemIndex)
                      }
                      disabled={item.key === "order_id" || item.key === "name"}
                      sx={{ p: "0px 9px" }}
                    />
                  }
                  sx={{ width: "fit-content" }}
                />
              </Grid>
            ))}
          </Grid>
        </FormGroup>
      </>
    );
  };

  return (
    <>
      {exportItems && (
        <ExcelExport
          tableData={ordersColumnsList.filter((column) => column.export)}
          listingData={ordersList}
          exportType={"OrdersList"}
          buttonText={"Export Orders"}
          setExportType={setExportType}
        />
      )}

      {toggleItemsModal && (
        <ScrollableModal
          open={toggleItemsModal}
          onClose={() => {
            handleToggleItemsModal();
            setExportType("import_export");
          }}
          paperStyle={{ width: "70vw" }}
          scrollbarStyle={{ maxHeight: "94vh" }}
        >
          <Grid container>
            <Grid item xs={12} sx={headerGrid12Style}>
              <Typography variant="h2" sx={{ color: "#212121" }}>
                Export Custom Columns
              </Typography>

              <IconButton
                onClick={() => {
                  handleToggleItemsModal();
                  setExportType("import_export");
                }}
                sx={{ p: "unset" }}
              >
                <CloseIcon htmlColor="#D84315" fontSize="large" />
              </IconButton>
            </Grid>
          </Grid>

          <Divider sx={{ m: "16px 40px 24px" }} />

          <Stack sx={{ p: "0 40px 40px" }}>
            <Grid container>
              <Grid item md={12}>
                {exportUIList(ordersColumnsList)}
              </Grid>
            </Grid>

            <Grid container>
              <Grid item xs={12} sx={footerGrid12Style}>
                <Button
                  variant={"contained"}
                  sx={footerCancelBtnStyle}
                  onClick={() => {
                    handleToggleItemsModal();
                    setExportType("import_export");
                  }}
                >
                  Cancel
                </Button>

                <Button
                  variant={"contained"}
                  sx={{ p: "12px 24px", ml: "12px" }}
                  color={"secondary"}
                  onClick={handleItemsExport}
                >
                  Export Items
                </Button>
              </Grid>
            </Grid>
          </Stack>
        </ScrollableModal>
      )}
    </>
  );
};

export default CustomColumnsExport;
